import { useState, useEffect, useRef, useCallback } from "react";
import { Form } from "react-bootstrap";
import { Spinner } from "react-bootstrap";

/**
 * InfiniteScrollSelect - A custom dropdown component with infinite scroll support
 * @param {Object} props
 * @param {Array} props.options - Array of options to display
 * @param {string} props.value - Selected value
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.hasMore - Whether there are more items to load
 * @param {boolean} props.isLoading - Whether data is currently loading
 * @param {Function} props.onLoadMore - Callback to load more items
 * @param {string} props.placeholder - Placeholder text
 * @param {Function} props.getOptionLabel - Returns display label for an option
 * @param {Function} props.getOptionValue - Returns value for an option
 */
const InfiniteScrollSelect = ({
  options = [],
  value,
  onChange,
  disabled = false,
  className = "",
  hasMore = false,
  isLoading = false,
  onLoadMore,
  placeholder = "Select...",
  getOptionLabel = (option) =>
    option?.name ?? option?.label ?? option?.title ?? String(option ?? ""),
  getOptionValue = (option) => option?.value ?? option?.id ?? option?.name,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter((option) => {
    const label = (getOptionLabel(option) ?? "").toString().toLowerCase();
    return label.includes(searchTerm.toLowerCase());
  });

  // Scroll event listener for infinite scroll
  useEffect(() => {
    if (!isOpen || !hasMore || isLoading || !onLoadMore) return;

    const handleScroll = () => {
      if (!listRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      // Load more when user scrolls within 50px of the bottom
      if (scrollHeight - scrollTop - clientHeight < 50) {
        onLoadMore();
      }
    };

    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (listElement) {
        listElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isOpen, hasMore, isLoading, onLoadMore]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
    setSearchTerm("");
  };

  const selectedOption = options.find((opt) => getOptionValue(opt) === value);

  return (
    <div ref={dropdownRef} className={`tw-relative ${className}`}>
      <div
        className={`tw-flex tw-items-center tw-justify-between tw-w-full tw-px-3 tw-py-2 tw-rounded tw-bg-white tw-cursor-pointer ${
          disabled ? "tw-opacity-50 tw-cursor-not-allowed" : "hover:tw-border-gray-400"
        }`}
        style={{
          border: "1px solid #dee2e6",
          minHeight: "38px",
        }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={value ? "tw-text-gray-900" : "tw-text-gray-500"}>
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <span className="tw-text-gray-400">▼</span>
      </div>

      {isOpen && (
        <div className="tw-absolute tw-z-50 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-shadow-lg tw-max-h-60 tw-overflow-hidden">
          <div className="tw-p-2 tw-border-b tw-border-gray-200">
            <Form.Control
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="tw-w-full"
              autoFocus
            />
          </div>
          <div
            ref={listRef}
            className="tw-overflow-y-auto tw-max-h-48"
            style={{ maxHeight: "12rem" }}
          >
            {filteredOptions.length === 0 ? (
              <div className="tw-p-3 tw-text-center tw-text-gray-500">
                {isLoading ? "Loading..." : "No options found"}
              </div>
            ) : (
              <>
                {filteredOptions.map((option) => (
                  <div
                    key={getOptionValue(option) ?? getOptionLabel(option)}
                    className={`tw-px-3 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100 ${
                      value === getOptionValue(option)
                        ? "tw-bg-blue-50 tw-font-semibold"
                        : ""
                    }`}
                    onClick={() => handleSelect(getOptionValue(option))}
                  >
                    {getOptionLabel(option)}
                  </div>
                ))}
                {hasMore && (
                  <div className="tw-p-2 tw-text-center">
                    {isLoading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <div className="tw-text-gray-500 tw-text-sm">Scroll for more...</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InfiniteScrollSelect;

